import React, { useState, useMemo, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Briefcase, FolderKanban, FileSpreadsheet,
  Layers, Box, FileText, ListTodo, AlertTriangle, GitBranch,
  FileCheck, Users, Database, ChevronLeft, ChevronRight, Menu, Building2,
  Settings, Shield, Upload, DollarSign, Rocket, Bell, Book, HelpCircle,
  Gauge, Target, GitCompare, Sparkles, Info, BookTemplate, Brain, Wand2, Activity,
  ClipboardList, Webhook, BellRing, BarChart3, TrendingUp, PieChart, Calendar, Clock,
  Package, Plug, FileCode, Lock, Building, ExternalLink, Inbox, Workflow,
  Bookmark, Pin, CheckSquare, CreditCard, Globe, FolderTree
} from 'lucide-react';
import { useProductMode } from '../context/ProductModeContext';
import { useAuth } from '../context/AuthContext';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { trackNavClick } from '../utils/telemetry';
import { api } from '../api/client';

// =============================================================================
// ENTERPRISE NAVIGATION IA - V1 Commercial Readiness
// Simplified to reduce cognitive load from 54 items to focused sections
// Primary actions surfaced, secondary features in sub-menus
// =============================================================================

const fullNavItems = [
  // 1. DASHBOARDS - Role-based entry points
  { 
    section: 'Dashboards', 
    sectionKey: 'dashboards',
    icon: Building,
    items: [
      { path: '/executive', label: 'Executive Hub', icon: Gauge, scopingHidden: true, rbacRoles: ['executive', 'pmo_power', 'pmo_standard', 'admin', 'sysadmin', 'program_admin', 'pm', 'viewer'] },
      { path: '/strategy-hub', label: 'Strategy Hub', icon: Target, scopingHidden: true, rbacRoles: ['executive', 'pmo_power', 'pmo_standard', 'admin', 'sysadmin', 'program_admin', 'pm', 'viewer'] },
      { path: '/dashboard/pmo', label: 'PMO Center', icon: BarChart3, scopingHidden: true, rbacRoles: ['executive', 'pmo_power', 'pmo_standard', 'admin', 'sysadmin', 'program_admin', 'pm', 'viewer'] },
      { path: '/dashboard/project-manager', label: 'My Projects', icon: FileSpreadsheet, scopingHidden: true },
      { path: '/ai/pm-assistant', label: 'PM Assistant', icon: Brain, scopingHidden: false, rbacRoles: ['pm', 'pmo_power', 'admin', 'sysadmin', 'program_admin', 'delivery_lead', 'business_analyst', 'executive', 'viewer'] },
      { path: '/notifications', label: 'Daily Signals', icon: BellRing, scopingHidden: true, rbacRoles: ['executive', 'pmo_power', 'pmo_standard', 'admin', 'sysadmin', 'program_admin', 'pm', 'viewer'] },
    ]
  },

  // 2. PORTFOLIO - Structure, demands, and planning
  { 
    section: 'Portfolio', 
    sectionKey: 'portfolio',
    icon: Briefcase,
    items: [
      { path: '/portfolio/hierarchy', label: 'Hierarchy', icon: Building2, scopingHidden: true, rbacRoles: ['pmo_power', 'pmo_standard', 'admin', 'sysadmin', 'program_admin', 'executive', 'pm', 'viewer'] },
      { path: '/demands', label: 'Demand Board', icon: ClipboardList, scopingHidden: false, rbacRoles: ['submitter', 'governance', 'pmo_power', 'pmo_standard', 'admin', 'sysadmin', 'executive', 'program_admin', 'pm', 'viewer'] },
      { path: '/demands/new', label: 'New Demand', icon: Sparkles, scopingHidden: false, rbacRoles: ['submitter', 'governance', 'pmo_power', 'admin', 'sysadmin', 'program_admin'] },
      { path: '/roadmap', label: 'Roadmap', icon: Calendar, scopingHidden: true, rbacRoles: ['pmo_power', 'pmo_standard', 'admin', 'sysadmin', 'executive', 'program_admin', 'pm', 'viewer'] },
      { path: '/scenarios', label: 'Scenario Planning', icon: GitBranch, scopingHidden: false, rbacRoles: ['pmo_power', 'admin', 'sysadmin', 'program_admin', 'executive', 'pm'] },
    ]
  },

  // 3. DELIVERY - Work execution (the core daily work area)
  { 
    section: 'Delivery', 
    sectionKey: 'delivery',
    icon: Package,
    items: [
      { path: '/work-items', label: 'Delivery Workspace', icon: Layers, scopingHidden: 'workItems' },
      { path: '/issues', label: 'Issues & Risks', icon: AlertTriangle, scopingHidden: true },
      { path: '/decisions', label: 'Decisions', icon: FileCheck, scopingHidden: true },
      { path: '/dependencies', label: 'Dependencies', icon: GitBranch, scopingHidden: true },
      { path: '/releases', label: 'Releases & Sprints', icon: Rocket, scopingHidden: true },
    ]
  },

  // 4. SCOPING - Requirements, baselines (for project setup)
  { 
    section: 'Scoping', 
    sectionKey: 'scoping',
    icon: Target,
    items: [
      { path: '/scope/workspace', label: 'Requirements', icon: Target, scopingHidden: false },
      { path: '/scope/baselines', label: 'Baselines', icon: GitCompare, scopingHidden: false },
      { path: '/ai-scoping-assistant', label: 'AI Copilot', icon: Sparkles, scopingHidden: false },
      { path: '/delivery/excel-intake', label: 'Data Import', icon: Upload, scopingHidden: true, rbacRoles: ['pmo_power', 'admin', 'sysadmin', 'program_admin'] },
      { path: '/consulting', label: 'Proposal Builder', icon: Wand2, scopingHidden: false, rbacRoles: ['admin', 'sysadmin', 'consulting_admin', 'pmo_power', 'program_admin', 'consultant', 'proposal_author'] },
    ]
  },

  // 5. GOVERNANCE - Benefits, decisions, compliance
  { 
    section: 'Governance', 
    sectionKey: 'governance',
    icon: Shield,
    items: [
      { path: '/governance/benefits', label: 'Benefits', icon: TrendingUp, scopingHidden: false, rbacRoles: ['governance', 'pmo_power', 'admin', 'sysadmin', 'executive', 'program_admin', 'pm'] },
      { path: '/governance/decision-ledger', label: 'Decision Ledger', icon: FileCheck, scopingHidden: false, rbacRoles: ['governance', 'pmo_power', 'admin', 'sysadmin', 'executive', 'program_admin', 'pm', 'viewer'] },
      { path: '/governance', label: 'Governance Actions', icon: Shield, scopingHidden: false, rbacRoles: ['governance', 'pmo_power', 'admin', 'sysadmin', 'program_admin', 'executive'] },
      { path: '/governance/intent-versioning', label: 'Intent History', icon: Clock, scopingHidden: false, rbacRoles: ['governance', 'pmo_power', 'admin', 'sysadmin', 'executive', 'program_admin', 'pm'] },
    ]
  },

  // 6. RESOURCES - Resource management
  { 
    section: 'Resources', 
    sectionKey: 'resources',
    icon: Users,
    items: [
      { path: '/resources/capacity', label: 'Capacity', icon: Gauge, scopingHidden: false },
      { path: '/resources/utilisation', label: 'Utilization', icon: LayoutDashboard, scopingHidden: false },
      { path: '/resources', label: 'Resource Registry', icon: Database, scopingHidden: false },
    ]
  },

  // 7. ANALYTICS - Reporting (combined from analytics + some admin)
  { 
    section: 'Analytics', 
    sectionKey: 'analytics',
    icon: PieChart,
    items: [
      { path: '/analytics', label: 'Dashboards', icon: LayoutDashboard, scopingHidden: false },
      { path: '/analytics/risk-matrix', label: 'Risk Matrix', icon: AlertTriangle, scopingHidden: false, rbacRoles: ['pmo_power', 'pmo_standard', 'pmo_analyst', 'governance', 'admin', 'sysadmin', 'executive', 'program_admin', 'pm', 'viewer'] },
      { path: '/analytics/delivery-throughput', label: 'Delivery Analytics', icon: TrendingUp, scopingHidden: false, rbacRoles: ['pmo_power', 'pmo_standard', 'admin', 'sysadmin', 'executive', 'program_admin', 'pm', 'viewer'] },
      { path: '/financial', label: 'Financials', icon: DollarSign, scopingHidden: false, rbacRoles: ['pmo_power', 'pmo_standard', 'pmo_analyst', 'governance', 'admin', 'sysadmin', 'executive', 'program_admin', 'pm', 'viewer'] },
    ]
  },

  // 8. ADMIN - Administration and configuration (Admin only - collapsed by default)
  { 
    section: 'Administration', 
    sectionKey: 'admin',
    icon: Settings,
    collapsedByDefault: true,
    items: [
      { path: '/admin', label: 'Admin Dashboard', icon: Settings, scopingHidden: false, rbacRoles: ['admin', 'sysadmin', 'program_admin'] },
      { path: '/admin/users', label: 'Users & Tenants', icon: Building2, scopingHidden: false, rbacRoles: ['admin', 'sysadmin', 'program_admin'] },
      { path: '/wbs-structure', label: 'WBS Configuration', icon: FolderTree, scopingHidden: false, rbacRoles: ['admin', 'sysadmin', 'program_admin'] },
      { path: '/trust/security-and-compliance', label: 'Trust Center', icon: Shield, scopingHidden: false, rbacRoles: ['admin', 'sysadmin', 'pmo_power', 'executive', 'program_admin', 'governance'] },
      { path: '/admin/integrations', label: 'Integrations', icon: Plug, scopingHidden: false, rbacRoles: ['admin', 'sysadmin', 'pmo_power', 'program_admin'] },
    ]
  },

  // PLATFORM ADMIN - Multi-tenant management (sysadmin only)
  { 
    section: 'Platform', 
    sectionKey: 'platform_admin',
    icon: Globe,
    isPlatformSection: true,
    items: [
      { path: '/platform/tenants', label: 'Tenants', icon: Building2, scopingHidden: false, rbacRoles: ['sysadmin'] },
      { path: '/platform/users', label: 'System Users', icon: Users, scopingHidden: false, rbacRoles: ['sysadmin'] },
      { path: '/admin/billing', label: 'Billing', icon: DollarSign, scopingHidden: false, rbacRoles: ['sysadmin'] },
    ]
  },

  // HELP - Documentation and support
  { 
    section: 'Help', 
    sectionKey: 'help',
    icon: HelpCircle,
    items: [
      { path: '/docs', label: 'Documentation', icon: Book, scopingHidden: false },
    ]
  },
];

const Sidebar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [pinnedViews, setPinnedViews] = useState([]);
  const location = useLocation();
  const { isScopingOnly, canGenerateWorkItems, productMode, getFeature } = useProductMode();
  const { hasRole, isPlatformAdmin, isImpersonating } = useAuth();

  // Fetch pinned saved views
  useEffect(() => {
    const fetchPinnedViews = async () => {
      try {
        const response = await api.getSavedViews(true);
        const views = response.data?.views || [];
        setPinnedViews(views.filter(v => v.is_pinned));
      } catch (error) {
        console.debug('Failed to load pinned views:', error);
      }
    };
    
    fetchPinnedViews();
    
    // Listen for pin updates
    const handlePinUpdate = () => fetchPinnedViews();
    window.addEventListener('savedViewsUpdated', handlePinUpdate);
    return () => window.removeEventListener('savedViewsUpdated', handlePinUpdate);
  }, []);

  // Handle nav click with telemetry
  const handleNavClick = (path) => {
    trackNavClick(path, productMode);
  };

  // Filter navigation items based on product mode, feature flags, and RBAC
  const navItems = useMemo(() => {
    const filterItems = (items) => items.filter(item => {
      // Check feature flag if specified
      if (item.featureFlag) {
        const isEnabled = getFeature(item.featureFlag, false);
        if (!isEnabled) return false;
      }
      
      // Check RBAC roles if specified
      if (item.rbacRoles && item.rbacRoles.length > 0) {
        const userHasRole = hasRole(...item.rbacRoles);
        if (!userHasRole) return false;
      }
      
      // Check if item should be hidden in scoping mode
      if (isScopingOnly) {
        if (item.scopingHidden === true) return false;
        if (item.scopingHidden === 'workItems' && !canGenerateWorkItems) return false;
      }
      return true;
    });
    
    return fullNavItems.map(section => ({
      ...section,
      items: filterItems(section.items)
    })).filter(section => section.items.length > 0);
  }, [isScopingOnly, canGenerateWorkItems, getFeature, hasRole]);

  // Get hidden items for showing disabled links with tooltips
  const hiddenItems = useMemo(() => {
    if (!isScopingOnly) return [];
    
    return fullNavItems.flatMap(section => 
      section.items.filter(item => {
        if (item.scopingHidden === true) return true;
        if (item.scopingHidden === 'workItems' && !canGenerateWorkItems) return true;
        return false;
      })
    );
  }, [isScopingOnly, canGenerateWorkItems]);

  const isItemHidden = (path) => hiddenItems.some(item => item.path === path);

  return (
    <TooltipProvider>
      {/* Mobile menu button */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-[var(--clr-surface-card)] rounded-lg shadow-[var(--shadow-md)]"
        data-testid="mobile-menu-btn"
      >
        <Menu className="w-5 h-5 text-[var(--clr-text-primary)]" />
      </button>

      {/* Sidebar */}
      <aside
        className={`
          fixed left-0 top-0 h-screen bg-[var(--clr-nav-bg)] text-white z-40
          transition-all duration-300 flex flex-col
          ${collapsed ? 'w-16' : 'w-64'}
          ${collapsed ? '-translate-x-full lg:translate-x-0' : 'translate-x-0'}
        `}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className={`p-4 border-b border-[var(--clr-nav-item)]/20 flex items-center ${collapsed ? 'justify-center' : 'justify-between'}`}>
          {!collapsed && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[var(--clr-brand-primary)] rounded-lg flex items-center justify-center font-bold text-white">
                S
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-lg leading-tight">ScopeOps</span>
                {isScopingOnly && (
                  <span className="text-[10px] text-[var(--clr-brand-primary)] font-medium -mt-0.5">Scoping</span>
                )}
              </div>
            </div>
          )}
          {collapsed && (
            <div className="w-8 h-8 bg-[var(--clr-brand-primary)] rounded-lg flex items-center justify-center font-bold text-white">
              S
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4">
          {navItems.map((section) => {
            // Check if this is a platform section and user is impersonating
            const showPlatformSeparator = section.isPlatformSection && isPlatformAdmin() && isImpersonating();
            
            return (
              <div key={section.section}>
                {/* Platform section separator when impersonating */}
                {showPlatformSeparator && !collapsed && (
                  <div className="mx-2 my-4 border-t border-dashed border-amber-500/50">
                    <div className="flex items-center gap-2 px-2 pt-3 pb-1">
                      <Globe className="w-3 h-3 text-amber-400" />
                      <span className="text-[10px] text-amber-400 font-medium uppercase tracking-wider">
                        Global Platform
                      </span>
                    </div>
                  </div>
                )}
                
                <div className="mb-4">
                  {!collapsed && (
                    <p className={`px-4 text-xs font-semibold uppercase tracking-wider mb-2 ${
                      showPlatformSeparator ? 'text-amber-400' : 'text-[var(--clr-nav-section)]'
                    }`}>
                      {section.section}
                    </p>
                  )}
                  <ul className="space-y-1 px-2">
                    {section.items.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.path;
                      const isPlatformItem = section.isPlatformSection;
                      
                      return (
                        <li key={item.path}>
                          <NavLink
                            to={item.path}
                            onClick={() => handleNavClick(item.path)}
                            className={`
                              flex items-center gap-3 px-3 py-2 rounded-lg transition-colors
                              ${isActive 
                                ? isPlatformItem
                                  ? 'bg-amber-600 text-white'
                                  : 'bg-[var(--clr-brand-primary)] text-[var(--clr-nav-item-active)]'
                                : isPlatformItem
                                  ? 'text-amber-300/80 hover:bg-amber-600/20 hover:text-amber-200'
                                  : 'text-[var(--clr-nav-item)] hover:bg-[var(--clr-nav-item-active-bg)] hover:text-[var(--clr-nav-item-hover)]'}
                              ${collapsed ? 'justify-center' : ''}
                            `}
                            title={collapsed ? item.label : undefined}
                            data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
                          >
                            <Icon className="w-5 h-5 flex-shrink-0" />
                            {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
                          </NavLink>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            );
          })}
          
          {/* Sprint 24 P0: My Views moved to inside Delivery Workspace page */}
          {/* Saved views are now accessible via Delivery → Delivery Workspace */}
          
          {/* Show disabled links section in scoping mode */}
          {isScopingOnly && !collapsed && hiddenItems.length > 0 && (
            <div className="mb-4 px-2 pt-4 border-t border-[var(--clr-nav-item)]/20">
              <p className="px-2 text-[10px] font-medium text-[var(--clr-nav-section)]/60 uppercase tracking-wider mb-2 flex items-center gap-1">
                <Info className="w-3 h-3" />
                Not available in Scoping Mode
              </p>
              <ul className="space-y-0.5">
                {hiddenItems.slice(0, 5).map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.path}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div
                            className="flex items-center gap-3 px-3 py-1.5 rounded-lg text-[var(--clr-nav-item)]/40 cursor-not-allowed"
                            data-testid={`nav-disabled-${item.path.replace('/', '')}`}
                          >
                            <Icon className="w-4 h-4 flex-shrink-0" />
                            <span className="text-xs">{item.label}</span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-xs">
                          <p className="text-xs">Not available in Scoping Mode.</p>
                        </TooltipContent>
                      </Tooltip>
                    </li>
                  );
                })}
                {hiddenItems.length > 5 && (
                  <li className="px-3 py-1 text-[10px] text-[var(--clr-nav-item)]/30">
                    +{hiddenItems.length - 5} more features
                  </li>
                )}
              </ul>
            </div>
          )}
        </nav>

        {/* Collapse Toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex items-center justify-center p-3 border-t border-[var(--clr-nav-item)]/20 hover:bg-[var(--clr-nav-item-active-bg)] transition-colors"
          data-testid="collapse-btn"
        >
          {collapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
          {!collapsed && <span className="ml-2 text-sm">Collapse</span>}
        </button>
      </aside>

      {/* Mobile overlay */}
      {!collapsed && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setCollapsed(true)}
        ></div>
      )}
    </TooltipProvider>
  );
};

export default Sidebar;
